import * as React from "react";
import Image from "next/image";
import { ArrowRight, X } from "lucide-react";
import { getVideoEmbedUrl, getVideoThumbnailUrl, VIMEO_EMBED_URL, YOU_TUBE_VIDEO_URL } from "@/lib/api/endpoint";

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedImage: string | null;
  images: any[];
  setSelectedImage: (url: string | null) => void;
}

const THUMBNAIL_WIDTH = 100;
const THUMBNAIL_MARGIN = 8;

const isVideo = (url: string) =>
  url?.includes(YOU_TUBE_VIDEO_URL) || url?.includes(VIMEO_EMBED_URL);


const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  selectedImage,
  images = [],
  setSelectedImage,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [direction, setDirection] = React.useState<number>(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [displayedImage, setDisplayedImage] = React.useState<string | null>(selectedImage);
  const thumbnailContainerRef = React.useRef<HTMLDivElement>(null);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Prevent background scroll when modal is open

React.useEffect(() => {
  if (isOpen) {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden"; 
  } else {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }
  return () => {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  };
}, [isOpen]);

  // Focus trap
  React.useEffect(() => {
    if (!isOpen) return;
    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.[0]?.focus();
    const handleTab = (e: KeyboardEvent) => {
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    window.addEventListener("keydown", handleTab);
    return () => window.removeEventListener("keydown", handleTab);
  }, [isOpen]);

  // Update current index when selectedImage changes
  React.useEffect(() => {
    if (selectedImage && images?.length) {
      const index = images.findIndex((image: any) => {
        const url = image?.url ?? image?.video_url;
        return url === selectedImage;
      });
      if (index !== -1) setCurrentIndex(index);
    }
  }, [selectedImage, images]);

  // Custom transition effect for image/video change
  React.useEffect(() => {
    if (selectedImage !== displayedImage) {
      setIsTransitioning(true);
      const timeout = setTimeout(() => {
        setDisplayedImage(selectedImage);
        setIsTransitioning(false);
      }, 200); // 200ms fade duration
      return () => clearTimeout(timeout);
    }
  }, [selectedImage, displayedImage]);

  // Scroll thumbnails into view
  React.useEffect(() => {
    if (thumbnailContainerRef.current && images?.length) {
      const containerWidth = thumbnailContainerRef.current.clientWidth;
      const scrollTo =
        currentIndex * (THUMBNAIL_WIDTH + THUMBNAIL_MARGIN) -
        containerWidth / 2 +
        THUMBNAIL_WIDTH / 2;
      thumbnailContainerRef.current.scrollTo({
        left: scrollTo,
        behavior: "smooth",
      });
    }
  }, [currentIndex, images?.length]);

  const handleNext = React.useCallback(() => {
    setDirection(1);
    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(nextIndex);
    setSelectedImage(images[nextIndex]?.url ?? images[nextIndex]?.video_url);
  }, [currentIndex, images, setSelectedImage]);

  const handlePrevious = React.useCallback(() => {
    setDirection(-1);
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedImage(images[prevIndex]?.url ?? images[prevIndex]?.video_url);
  }, [currentIndex, images, setSelectedImage]);

  // Keyboard navigation
  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleNext, handlePrevious, onClose]);

  if (!isOpen || !images?.length) return null;
  return (
    <div
      ref={modalRef}
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
      aria-labelledby="image-modal-title"
      aria-describedby="image-modal-description"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm"
    >
      <div className="relative mx-auto w-full max-w-screen-md rounded  px-4  outline-none md:px-6">
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close gallery"
          className="absolute right-2 top-[-20px] z-20 rounded-lg bg-black/60 p-1 text-white hover:bg-black/80 focus:outline-none md:right-0 md:p-2"
        >
          <X />
        </button>

        {/* Main Image or Video */}
        <div className="relative flex h-48 items-center justify-center px-4 sm:h-72 md:h-[450px]">
          <div
            className={`absolute flex size-full items-center justify-center transition-opacity duration-200 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
            style={{ pointerEvents: isTransitioning ? 'none' : 'auto' }}
          >
            {displayedImage && (
              isVideo(displayedImage) ? (
                <iframe
                  src={getVideoEmbedUrl(displayedImage) || ""}
                  className="h-[160px] w-[280px] object-cover sm:h-[250px] sm:w-[400px] md:h-[400px] md:w-[650px]"
                  allowFullScreen
                  title="Gallery Video"
                  tabIndex={0}
                  loading="lazy"
                ></iframe>
              ) : (
                <Image
                  src={displayedImage}
                  alt="Selected"
                  height={1000}
                  width={1000}
                  className="h-[160px] w-[280px] border object-cover sm:h-[250px] sm:w-[400px] md:h-[400px] md:w-[650px]"
                  style={{ objectFit: "cover" }}
                  loading="lazy"
                />
              )
            )}
          </div>
        </div>

        {/* Thumbnail List */}
        <div
          ref={thumbnailContainerRef}
          className="hide-scrollbar mt-4 flex w-full gap-1 overflow-x-auto  md:mt-5"
          style={{
            height: 80,
            whiteSpace: "nowrap",
            alignItems: "center",
          }}
          aria-label="Gallery thumbnails"
        >
          {images?.map((image: any, index: number) => {
            const thumbnailUrl = isVideo(image?.video_url)
              ? getVideoThumbnailUrl(image?.video_url)
              : image?.url;
            return (
              <div
                key={image?.url || image?.video_url || index}
                className="relative mr-3 flex w-full gap-x-4"
                style={{
                  display: "inline-block",
                  width: THUMBNAIL_WIDTH,
                  height: 60,
                  cursor: "pointer",
                  border: currentIndex === index ? "2px solid white"  : "none",
                  opacity: currentIndex === index ? 1 : 0.7, 
                }}
                tabIndex={0}
                aria-label={`Show ${isVideo(thumbnailUrl) ? "video" : "image"} ${index + 1}`}
                onClick={() => {
                  setCurrentIndex(index);
                  setSelectedImage(image?.url || image?.video_url);
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    setCurrentIndex(index);
                    setSelectedImage(image?.url || image?.video_url);
                  }
                }}
              >
                {currentIndex !== index && (
                  <div className="absolute left-0 top-0 z-10 size-full bg-black/60"></div>
                )}
                <Image
                  src={thumbnailUrl}
                  alt={`Thumbnail ${index + 1}`}
                  height={1000}
                  width={1000}
                  className="size-full min-w-[96px] object-fill"
                  loading="lazy"
                />
              </div>
            );
          })}
        </div>

        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          aria-label="Previous image"
          className="absolute left-2 top-1/3 z-10 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none md:-left-2 md:top-1/2"
        >
          <ArrowRight className="rotate-180" />
        </button>

        {/* Next Button */}
        <button
          onClick={handleNext}
          aria-label="Next image"
          className="absolute right-0 top-1/3 z-10 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none  md:right-2 md:top-1/2"
        >
          <ArrowRight />
        </button>
      </div>
    </div>
  );
};

export default React.memo(ImageModal);